/**
 * @voltxampere/presets — Transistor switch (NPN saturation) (#13)
 *
 * Classic BJT logic switch: V_in (HIGH = 5 V) drives the base
 * via Rb = 10 kΩ; the collector pulls up to Vcc = 5 V through
 * RL = 1 kΩ, with the load nominally at the collector node. With
 * I_B ≈ 0.43 mA × Bf (= 100) → I_C demand = 43 mA, far above the
 * saturation current Vcc/RL = 5 mA, so the device saturates and
 * V(collector) collapses toward V_CE,sat ≈ 0.2 V.
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. Vcc on the right
 * top with RL (rot 90) feeding Q1's collector, Vin (rot 90) at the
 * left in line with Rb (rot 0) → Q1 base. Q1 emitter wires
 * directly to GND. BJT pin coords derive from PIN_LOCAL_BY_TYPE
 * (B=(10,13), C=(32,4), E=(32,22)) shifted by viewBox center.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const transistorSwitch = {
    id: "transistor-switch",
    name: { tr: "BJT Anahtarlama (Doyum)", en: "BJT Switch (Saturation)" },
    category: "digital",
    difficulty: 3,
    description: {
        tr: "NPN BJT anahtar, V_in HIGH → Q1 doyuma girer. V_collector ≈ 0.2V.",
        en: "NPN BJT switch, V_in HIGH → Q1 saturates. V_collector ≈ 0.2V.",
    },
    circuit: makeCircuit({
        slot: 13,
        name: "BJT Switch",
        description: "NPN logic-level switch — saturation",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "vin" }),
            makeNet({ name: "b" }),
            makeNet({ name: "c" }),
        ],
        components: [
            makeComponent({
                id: "Vcc",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "Vcc",
                position: { x: 200, y: -120 },
                rotation: 0,
            }),
            makeComponent({
                id: "Vin",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_vin", neg: "net_gnd" },
                label: "Vin",
                position: { x: -200, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rb",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_vin", p2: "net_b" },
                label: "Rb",
                position: { x: -100, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "RL",
                type: "resistor",
                params: { resistance: 1_000 },
                terminals: { p1: "net_vcc", p2: "net_c" },
                label: "RL",
                position: { x: 60, y: -90 },
                rotation: 90,
            }),
            makeComponent({
                id: "Q1",
                type: "bjt-npn",
                params: {},
                terminals: { base: "net_b", collector: "net_c", emitter: "net_gnd" },
                label: "Q1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -100, y: 160 },
                rotation: 0,
            }),
        ],
        wires: [
            // vcc rail: Vcc.pos (200, -130) → RL.p1 (60, -110)
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: 200, y: -130 },
                    { x: 200, y: -150 },
                    { x: 60, y: -150 },
                    { x: 60, y: -110 },
                ],
                style: "orthogonal",
            },
            // vin net: Vin.pos (-190, 0) → Rb.p1 (-120, 0)
            {
                id: "w_vin",
                netId: "net_vin",
                path: [
                    { x: -190, y: 0 },
                    { x: -120, y: 0 },
                ],
                style: "orthogonal",
            },
            // base net: Rb.p2 (-80, 0) → Q1.B (-10, 0)
            {
                id: "w_b",
                netId: "net_b",
                path: [
                    { x: -80, y: 0 },
                    { x: -10, y: 0 },
                ],
                style: "orthogonal",
            },
            // collector net: RL.p2 (60, -70) → Q1.C (12, -9)
            {
                id: "w_c",
                netId: "net_c",
                path: [
                    { x: 60, y: -70 },
                    { x: 60, y: -9 },
                    { x: 12, y: -9 },
                ],
                style: "orthogonal",
            },
            // gnd leg A — Q1.E (12, 9) → GND.p (-100, 151)
            {
                id: "w_gnd_e",
                netId: "net_gnd",
                path: [
                    { x: 12, y: 9 },
                    { x: 12, y: 100 },
                    { x: -100, y: 100 },
                    { x: -100, y: 151 },
                ],
                style: "orthogonal",
            },
            // gnd leg B — Vin.neg (-210, 0) → GND.p
            {
                id: "w_gnd_vin",
                netId: "net_gnd",
                path: [
                    { x: -210, y: 0 },
                    { x: -210, y: 151 },
                    { x: -100, y: 151 },
                ],
                style: "orthogonal",
            },
            // gnd leg C — Vcc.neg (200, -110) → GND.p
            {
                id: "w_gnd_vcc",
                netId: "net_gnd",
                path: [
                    { x: 200, y: -110 },
                    { x: 200, y: 220 },
                    { x: -100, y: 220 },
                    { x: -100, y: 151 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        // V_CE,sat is engine-dependent; the Sprint 7 Ebers-Moll stamp
        // settles around 100-300 mV in saturation. The ±0.4 V band
        // tolerates the nonzero V_BC at full turn-on (Sprint 8c
        // discipline note "V_BC = 0 knee residual parity").
        {
            node: "c",
            analysis: "dc",
            value: 0.2,
            tolerance: 0.4,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "BJT doyum: V_CE < 0.3V → Q1 sayısal LOW olarak davranır.",
            en: "BJT saturation: V_CE < 0.3V → Q1 acts as a digital LOW.",
        },
        {
            tr: "Beta'nın doyumda anlamı yok: Ic taban tarafından sınırlanmaz.",
            en: "Beta is irrelevant in saturation: Ic is not base-limited.",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, §2.2"],
};
//# sourceMappingURL=transistor-switch.js.map